import React, {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import Checkbox from '@/components/ui/checkbox';
import { ColumnDef, Row } from '@tanstack/react-table';
import { Link, useLocation } from 'react-router-dom';
import { formatString } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Check, MinusIcon, PlusIcon, X } from 'lucide-react';
import { cn } from '@/lib/utils/global';

export type ActionType<T> = {
  element: FC<{ data: T; cb?: () => void; currentRow: number }>;
  colName?: string;
  actionCb?: () => void;
};

export type TableColumnsProps<T = Record<string, unknown>> = {
  cols?: Row<T>['original'][];
  query: string[];
  idToEdit?: number;
  showSelect?: boolean;
  expansionDepth?: number;
  inputType?: string;
  showFull?: boolean;
  customId?: string;
  expandableRows?: boolean;
  setIdToEdit?: Dispatch<SetStateAction<number | undefined>>;
  originalCols?: string[];
  colsToFormat?: {
    cols: string[];
    formatterFunc: (val: string) => string;
  };
  checkboxKeysArray?: string[];
  datePickerKeysArray?: string[];
  onBlur?: (id: number, data: T[], row: Row<T>) => void;
  getSelectedRows?: (rows: T[]) => void;
  getLink?: (id: string) => string | { pathname: string; state: object };
  action?: ActionType<Row<T>['original']>[]; // Custom actions for rows
  handleValueChange?: (data: T[]) => void; // Callback for handling updated data
  linkColumns?: Record<
    string,
    (rowData: T) => void // Modified to allow custom actions
  >;
  setApiUrl?: (url: string) => void; // Pass the API URL setter
  setModalOpen?: (open: boolean) => void;
};

function useGetTableColumns<T>({
  cols,
  query,
  checkboxKeysArray,
  datePickerKeysArray,
  colsToFormat,
  expansionDepth,
  originalCols,
  showSelect,
  showFull,
  expandableRows,
  customId,
  getLink,
  linkColumns,
  handleValueChange,
  idToEdit,
  onBlur,
  getSelectedRows,
  setIdToEdit,
  action
}: TableColumnsProps<T>) {
  const id = customId || 'id';
  const { pathname } = useLocation();
  const [partialFormatCols, setPartialFormatCols] = useState(false);
  const [data, setData] = useState<T[]>( cols ?? []);
  const inputRefs = useRef<Map<string, HTMLInputElement>>(new Map());
  const [refKey, setRefKey] = useState<string>('');

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    row: Row<T>,
    colName: keyof T
  ) => {
    const value = checkboxKeysArray?.includes(colName as string)
      ? event.target.checked
      : event.target.value;

    setRefKey(`${row.original[id as keyof T]}_${String(colName)}`);

    // Ensure state update is immutable and properly triggers a re-render
    setData((prevData) =>
      prevData.map((item) =>
        item[id as keyof T] === row.original[id as keyof T]
          ? { ...item, [colName]: value } // Correctly update the edited row
          : item
      )
    );
  };

  const handleDateChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    row: Row<T>,
    key: keyof T
  ) => {
    let value = e.target.value;

    if (e.target.type === 'datetime-local') {
      const date = new Date(value);
      value = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
        .toISOString()
        .replace('.000Z', '+00:00');
    }

    setRefKey(`${row.original[id as keyof T]}_${String(key)}`);
    setData((prevData) =>
      prevData.map((item) =>
        item[id as keyof T] === row.original[id as keyof T]
          ? { ...item, [key]: value }
          : item
      )
    );
  };

  useEffect(() => {
    const focusedInput = inputRefs.current.get(refKey);
    if (focusedInput) {
      focusedInput.focus();
    }
  }, [data]);

  useEffect(() => {
    const element = document.getElementById('command-details-modal');
    if (element || pathname.includes('meter-profile-data')) {
      setPartialFormatCols(true);
    }
  }, [pathname]);

  useEffect(() => {
    if (cols && JSON.stringify(cols) !== JSON.stringify(data)) {
      setData(cols);
    }
  }, [cols]);

  const handleAbort = () => {
    if (data.length > 1) {
      setIdToEdit?.(undefined);
    }
    setData(cols || []);
  };

  const submit = (row: Row<T>) => {
    handleValueChange?.(data);
    onBlur?.(Number(row.original[id as keyof T]), data, row);
    setIdToEdit?.(undefined);
  };

  const getRowValue = (rowValue: string, colName: string): JSX.Element => {
    const [showFullValue, setShowFullValue] = useState(false);
    let displayValue = rowValue;

    if (
      rowValue === '65535-00-00 00:00:00' ||
      rowValue === '1970-01-01 00:00:00'
    ) {
      displayValue = '-';
    } else if (
      colsToFormat &&
      colsToFormat.cols.includes(colName) &&
      rowValue !== 'null'
    ) {
      displayValue = colsToFormat.formatterFunc(rowValue);
    }
    const valueToShow =
      showFullValue || displayValue.length <= 30
        ? displayValue
        : `${displayValue.slice(0, 5)}...${displayValue.slice(-5)}`;

    return (
      <span
        onClick={() => setShowFullValue((prev) => !prev)}
        style={{ cursor: displayValue.length > 20 ? 'pointer' : 'default' }}
        title={'Click to expand'}>
        {valueToShow}
      </span>
    );
  };

  const columns: ColumnDef<T>[] = useMemo(() => {
    if (!cols || cols.length === 0) return [];

    const initialRow = data[0] ?? (cols[0] as T[]);

    //adding all generic columns
    const genericCols: ColumnDef<T>[] = Object.keys(initialRow)
      .filter((item) => !query.includes(item))
      .map((columnName) => {
        const formattedColName = columnName.replaceAll('_', ' ');
        return {
          accessorKey: columnName,
          header: partialFormatCols
            ? columnName.replaceAll('_', ' ')
            : !originalCols?.includes(columnName)
            ? formatString(formattedColName)
            : formattedColName,
          cell: ({ row }) => {
            const rowCopy = { ...row };
            const rowVal = String(row.getValue(columnName));
            return (
              <div className="capitalize">
                {Number(row.original[id as keyof T]) === idToEdit ||
                checkboxKeysArray?.includes(columnName) ? (
                  <div>
                    {checkboxKeysArray?.includes(columnName) ? (
                      <div className="cl-toggle-switch">
                        <label className="cl-switch">
                          <input
                            type="checkbox"
                            id={`${row.original[id as keyof T]}_${columnName}`}
                            checked={(() => {
                              if (checkboxKeysArray.includes(columnName)) {
                                const checkboxValue = data.find(
                                  (e) =>
                                    e[id as keyof T] ===
                                    row.original[id as keyof T]
                                )?.[columnName as keyof typeof row.original];
                                return Boolean(checkboxValue);
                              }
                              return false;
                            })()}
                            disabled={
                              idToEdit !== Number(row.original[id as keyof T])
                            }
                            onChange={(e) =>
                              handleChange(e, rowCopy, columnName as keyof T)
                            }
                          />
                          <span></span>
                        </label>
                      </div>
                    ) : datePickerKeysArray?.includes(columnName) &&
                      idToEdit === Number(row.original[id as keyof T]) ? (
                      <Input
                        id={`${row.original[id as keyof T]}_${columnName}`}
                        type="datetime-local"
                        name={String(columnName)}
                        value={(() => {
                          const cellValue = data.find(
                            (e) =>
                              e[id as keyof T] === row.original[id as keyof T]
                          )?.[columnName as keyof typeof row.original];
                          return cellValue
                            ? new Date(cellValue as string)
                                .toISOString()
                                .slice(0, 16)
                            : '';
                        })()}
                        className="border p-2 rounded"
                        onChange={(e) =>
                          handleDateChange(e, rowCopy, columnName as keyof T)
                        }
                      />
                    ) : (
                      <Input
                        id={`${row.original[id as keyof T]}_${columnName}`}
                        value={(() => {
                          const cellValue = data.find(
                            (e) =>
                              e[id as keyof T] === row.original[id as keyof T]
                          )?.[columnName as keyof typeof row.original];
                          return typeof cellValue === 'string' ||
                            typeof cellValue === 'number' ||
                            Array.isArray(cellValue)
                            ? cellValue
                            : '';
                        })()}
                        ref={(el) => {
                          if (el) {
                            inputRefs.current.set(el.id, el);
                          }
                        }}
                        onChange={(e) =>
                          handleChange(e, rowCopy, columnName as keyof T)
                        }
                        type={(() => {
                          const cellValue = data.find(
                            (e) =>
                              e[id as keyof T] === row.original[id as keyof T]
                          )?.[columnName as keyof typeof row.original];
                          return typeof cellValue === 'number'
                            ? 'number'
                            : 'text';
                        })()}
                      />
                    )}
                  </div>
                ) : showFull ? (
                  rowVal
                ) : (
                  <div>
                    {linkColumns && linkColumns[columnName] ? (
                      <span
                        onClick={() => {
                          linkColumns[columnName](row.original);
                        }}
                        style={{
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          fontWeight: 'bold'
                        }}>
                        {getRowValue(rowVal, columnName)}
                      </span>
                    ) : (
                      getRowValue(rowVal, columnName)
                    )}
                  </div>
                )}
              </div>
            );
          }
        };
      });

    if (expandableRows) {
      genericCols.unshift({
        accessorKey: `action_key_expandable_row_${id}`,
        header: '',
        cell: ({ row }) => {
          return row.getCanExpand() ? (
            <button
              id={`action_key_expandable_row_${id}`}
              onClick={row.getToggleExpandedHandler()}
              style={{ cursor: 'pointer' }}>
              {row.getIsExpanded() ? (
                <div
                  className={cn(
                    'bg-[#0A3690] flex justify-center items-center w-[30px] h-[30px] rounded-full',
                    expansionDepth === 1
                      ? 'bg-[#0A3690]'
                      : expansionDepth === 2
                      ? 'bg-[#00C4B4]'
                      : ''
                  )}>
                  <MinusIcon color="white" />
                </div>
              ) : (
                <div
                  className={cn(
                    'bg-[#0A3690] flex justify-center items-center w-[30px] h-[30px] rounded-full',
                    expansionDepth === 1
                      ? 'bg-[#0A3690]'
                      : expansionDepth === 2
                      ? 'bg-[#00C4B4]'
                      : ''
                  )}>
                  <PlusIcon color="white" />
                </div>
              )}
            </button>
          ) : (
            ''
          );
        }
      });
    }

    //adding select feature
    if (showSelect) {
      genericCols.unshift({
        accessorKey: 'action_key_select',
        id: 'select',
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && 'indeterminate')
            }
            onCheckedChange={(value) => {
              table.toggleAllPageRowsSelected(!!value);
              setTimeout(() => {
                const selectedRows = table
                  .getSelectedRowModel()
                  .rows.map((r) => r.original);

                getSelectedRows?.(selectedRows);
              }, 0);
            }}
            aria-label="Select all"
          />
        ),

        cell: ({ row, table }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => {
              row.toggleSelected(!!value);

              // Use setTimeout to ensure state updates before getting selected rows
              setTimeout(() => {
                const selectedRows = table
                  .getSelectedRowModel()
                  .rows.map((r) => r.original);

                getSelectedRows?.(selectedRows);
              }, 0);
            }}
            aria-label="Select row"
          />
        ),

        enableSorting: false,
        enableHiding: false
      });
    }

    //adding link feature
    if (getLink) {
      const linkFx = getLink;
      genericCols.push({
        accessorKey: `link_${id}_action_key`,
        header: '',
        cell: ({ row }) => {
          const rowId = row.getValue(id) as string;
          const linkProps = linkFx(rowId);
          return typeof linkProps === 'string' ? (
            <Link
              className="link-button tertiary-vee-btn px-2"
              to={linkProps}>
              Details
            </Link>
          ) : (
            <Link
              className="link-button tertiary-vee-btn px-2"
              to={linkProps.pathname}
              state={linkProps.state}>
              Details
            </Link>
          );
        }
      });
    }

    //adding action features
    if (action && action.length > 0) {
      const actionArr: ColumnDef<T>[] = action.map((actionData) => {
        const reactElement = actionData.element;
        const randomKey =
          (Math.random() + 1).toString(36).substring(7) +
          (actionData.colName || '');
        return {
          accessorKey: `${id}_${randomKey}_action_key`,
          header: actionData.colName,
          id: actionData.colName || `${id}_${randomKey}`,
          cell: ({ row }) => {
            const allData = row.original;
            return (
              <div
                className="flex"
                key={`${id}_${randomKey}`}>
                {reactElement({
                  data: allData,
                  cb: actionData.actionCb,
                  currentRow: row.index + 1
                })}
              </div>
            );
          }
        };
      });
      genericCols.push(...actionArr);
      if (idToEdit) {
        genericCols.push({
          accessorKey: `action_key_check-btn-${id}`,
          header: '',
          id: `check-btn-${id}`,
          cell: ({ row }) => (
            <div
              className={cn(
                'flex items-center gap-2',
                row.original[id as keyof T] === idToEdit ? '' : 'hidden'
              )}>
              <span title="Save changes">
                {' '}
                <Check
                  id={`save-row-${row.id}`}
                  className={`rounded-full bg-green-500 text-white  p-1`}
                  onClick={() => submit(row)}
                  size={32}
                />
              </span>
              <span title="Discard changes">
                <X
                  onClick={handleAbort}
                  id={`discard-row-${row.id}`}
                  className="rounded-full bg-red-500 text-white"
                  size={32}
                />
              </span>
            </div>
          )
        });
      }
    }
    return genericCols;
  }, [cols, showSelect, getLink, action, query, id, idToEdit]);

  return columns;
}

export default useGetTableColumns;
